import axios from "axios";

import { API_BASE } from "@/shared/api/client";
import type {
  BackgroundCheck,
  CompletedShiftRow,
  Invoice,
  InvoiceDetail,
  PendingWorker,
  UpcomingShiftRow,
  VenueRow,
  WorkerRow,
  WorkerStatus,
} from "@/features/ops/types";

function opsClient(token: string) {
  return axios.create({
    baseURL: `${API_BASE}/api/v1/ops`,
    headers: { "X-Ops-Token": token },
  });
}

export async function listPendingWorkers(token: string): Promise<PendingWorker[]> {
  const { data } = await opsClient(token).get<PendingWorker[]>("/workers/pending");
  return data;
}

export async function approveWorker(
  token: string,
  workerId: string,
  options: { acknowledgeFlagged?: boolean } = {},
): Promise<void> {
  await opsClient(token).post(`/workers/${workerId}/approve`, {
    acknowledge_flagged: options.acknowledgeFlagged ?? false,
  });
}

export async function runBackgroundCheck(
  token: string,
  workerId: string,
): Promise<BackgroundCheck> {
  const { data } = await opsClient(token).post<BackgroundCheck>(
    `/workers/${workerId}/background-check`,
  );
  return data;
}

export async function suspendWorker(token: string, workerId: string): Promise<void> {
  await opsClient(token).post(`/workers/${workerId}/suspend`);
}

export async function deleteWorker(token: string, workerId: string): Promise<void> {
  await opsClient(token).delete(`/workers/${workerId}`);
}

export async function deleteVenue(token: string, venueId: string): Promise<void> {
  await opsClient(token).delete(`/venues/${venueId}`);
}

export async function deleteShift(token: string, shiftId: string): Promise<void> {
  await opsClient(token).delete(`/shifts/${shiftId}`);
}

export async function getWorkerDetail(
  token: string,
  workerId: string,
): Promise<PendingWorker> {
  const { data } = await opsClient(token).get<PendingWorker>(`/workers/${workerId}`);
  return data;
}

export async function listAllWorkers(
  token: string,
  status?: WorkerStatus,
): Promise<WorkerRow[]> {
  const { data } = await opsClient(token).get<WorkerRow[]>("/workers", {
    params: status ? { status } : {},
  });
  return data;
}

export async function listUpcomingShifts(token: string): Promise<UpcomingShiftRow[]> {
  const { data } = await opsClient(token).get<UpcomingShiftRow[]>("/upcoming-shifts");
  return data;
}

export async function listPayouts(
  token: string,
  paid?: boolean,
): Promise<CompletedShiftRow[]> {
  const { data } = await opsClient(token).get<CompletedShiftRow[]>("/payouts", {
    params: paid !== undefined ? { paid } : {},
  });
  return data;
}

export async function markWorkerPaid(token: string, shiftId: string): Promise<void> {
  await opsClient(token).post(`/payouts/${shiftId}/mark-paid`);
}

export async function listInvoices(token: string): Promise<Invoice[]> {
  const { data } = await opsClient(token).get<Invoice[]>("/invoices");
  return data;
}

export async function getInvoiceDetail(token: string, id: string): Promise<InvoiceDetail> {
  const { data } = await opsClient(token).get<InvoiceDetail>(`/invoices/${id}`);
  return data;
}

export async function markInvoiceSent(token: string, id: string): Promise<Invoice> {
  const { data } = await opsClient(token).post<Invoice>(`/invoices/${id}/mark-sent`);
  return data;
}

export async function markInvoicePaid(token: string, id: string): Promise<Invoice> {
  const { data } = await opsClient(token).post<Invoice>(`/invoices/${id}/mark-paid`);
  return data;
}

export async function deleteInvoice(token: string, id: string): Promise<void> {
  await opsClient(token).delete(`/invoices/${id}`);
}

export async function listVenues(token: string): Promise<VenueRow[]> {
  const { data } = await opsClient(token).get<VenueRow[]>("/venues");
  return data;
}

export async function updateVenueMarkup(
  token: string,
  venueId: string,
  markup: string,
): Promise<void> {
  await opsClient(token).post(`/venues/${venueId}/markup`, { markup_percentage: markup });
}
